import React from "react";
import { useOutletContext } from "react-router-dom";
import { Award, Heart, Microscope, Sparkles } from "lucide-react";
import { IMAGES } from "../data/site";
import { SectionHeading } from "../components/SectionHeading";
import { Seo } from "../components/Seo";
import { SEO, personSchema, organizationSchema, breadcrumbSchema } from "../data/seo";

const FEATURES = [
  {
    icon: Award,
    title: "Dentistry should never feel routine.",
    body: "Every smile tells a different story. That is why every treatment begins with listening, understanding your goals, and designing a plan that protects your health while creating natural, lasting results.",
  },
  {
    icon: Heart,
    title: "Preserve before replacing.",
    body: "The strongest tooth is the one you were born with. Whenever possible, treatment is designed to conserve healthy tooth structure and restore function with the least invasive approach.",
  },
  {
    icon: Microscope,
    title: "Beautiful should never look artificial.",
    body: "Exceptional cosmetic dentistry is measured by what people do not notice. Every smile is designed to complement your facial features, personality, and natural expressions.",
  },
  {
    icon: Sparkles,
    title: "Every detail matters.",
    body: "Advanced technology, meticulous planning, and decades of clinical experience come together to deliver dentistry that is functional, durable, and exceptionally refined.",
  },
];

export const AboutPage = () => {
  const { openBooking } = useOutletContext();
  return (
  <div data-testid="about-page">
    <Seo
      title={SEO.about.title}
      description={SEO.about.description}
      image={IMAGES.Drk}
      type="profile"
      schemas={[
        personSchema(),
        organizationSchema(),
        breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }]),
      ]}
    />
    {/* Hero */}
    <section>
      <div className="relative h-[60vh] min-h-[360px] overflow-hidden">
      <img src={IMAGES.Drk} alt="Dr. K Portrait" className="absolute  inset-0 w-full h-full object-cover object-center "  
      style={{ objectPosition: "50% 25%" }}
      />
      {/* <div className="absolute inset-0 bg-gradient-to-l from-black/60 via-transparent to-transparent pointer-events-none" /> */}
      {/* <div className="absolute bottom-0 right-0 w-[40%] p-3 sm:p-6 lg:p-10 text-[#2727F5] break-words">
        <h1 className="heading-display font-semibold text-xl sm:text-3xl md:text-4xl xl:text-5xl">
          Behind every smile <span>there is a story.</span>
        </h1>
        <p className="font-serif italic text-sm sm:text-lg md:text-xl mt-4 md:mt-6">
        Behind every dentist, there is a purpose. 
        </p>
      </div> */}
      </div>
    </section>

    {/* Dr. K Difference */}
    <section className="py-5 container-x">
      <div className="max-w-4xl mx-auto flex flex-col gap-12">
        <div className="text-center reveal">
          <div className="label-eyebrow mb-6">The Dr.K Difference</div>
          <h2 className="heading-display text-4xl md:text-5xl lg:text-6xl">
            Dentistry, but practiced as <span className="italic text-gold">fine art.</span>
          </h2>
          
        </div>
        <div className="space-y-8 bg-gold/10 p-6 md:p-10 text-ink/80 text-lg leading-[1.8] reveal">
        <h2 className="heading-display text-3xl md:text-4xl lg:text-5xl">
            Meet Dr.K 
          </h2>
          <p>
            I became a dentist because I wanted to give people something that nothing else could — a reason to smile freely. Not a Hollywood smile. <em>Their</em> smile. The one they were meant to have.
          </p>
          <p>
            My journey began at New York University, where I earned my Bachelor of Arts in Science in 1997, followed by my Doctor of Dental Surgery degree from NYU College of Dentistry in 2002. What began as a fascination with science gradually became something much more meaningful: an opportunity to combine precision, artistry, and human connection in a way that could genuinely change people's lives.
          </p>
          <p>
            Over the years, I've learned that dentistry is about far more than teeth. It's about the person sitting across from me. Their experiences, their insecurities, their expectations, and sometimes, the courage it took just to walk through the door.
          </p>
          <p>
            I've always believed that the most important part of my work begins before I ever pick up an instrument. It begins with listening. Understanding what matters to someone, what makes them comfortable, and what they hope to feel when they look in the mirror. I want every patient to feel informed, reassured, and genuinely cared for, especially those who may have spent years feeling anxious about dentistry.
          </p>
          <p>
            My approach is deeply personal, shaped by years of clinical experience and a continuing dedication to learning. From implant dentistry and digital smile design to facial aesthetics and holistic approaches to oral health, I believe in looking beyond individual teeth to understand the relationship between health, function, beauty, and overall well-being.
          </p>
          <p>
            I have an appreciation for the smallest details, a respect for natural beauty, and a belief that exceptional dentistry should never look or feel artificial. The finest work is often the work no one notices. They simply notice you.
          </p>
          <p>
            What continues to inspire me is not just the transformation of a smile, but the transformation I see in the person behind it. The confidence to laugh without hesitation, to speak without self-consciousness, and to feel entirely comfortable being themselves.
          </p>
          <p className="font-serif italic text-2xl text-gold-dark border-l-2 border-gold pl-6 my-12">
            "A beautiful smile is the architecture of confidence. My job is to build it well."
          </p>
          <p className="text-sm tracking-widest uppercase text-ink/50">— Dr. Natalie Krasnyansky, DDS</p>
          <div className="border-t border-ink/10 pt-8">
            <h3 className="label-eyebrow mb-6">Credentials &amp; Affiliations</h3>
            <ul className="space-y-4 text-base">
              <li>1997 | Bachelor of Arts in Science, New York University</li>
              <li>2002 | Doctor of Dental Surgery, NYU College of Dentistry</li>
              <li>Academy of Facial Aesthetics</li>
              <li>Implant Dentistry</li>
              <li>Digital Smile Design</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* Why Dr. K Feature Grid */}
    <section className="py-24 md:py-32 bg-ink text-bone">
      <div className="container-x">
        <SectionHeading eyebrow="Why Dr. K" title="What you can" italicWord="expect." light />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="bg-ink p-10 md:p-14 reveal" data-testid={`feature-${i}`} style={{ transitionDelay: `${i * 60}ms` }}>
                {/* <Icon size={28} strokeWidth={1} className="text-gold mb-8" /> */}
                <h3 className="font-serif text-2xl md:text-3xl font-light leading-tight">{f.title}</h3>
                <p className="text-white/60 mt-4 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-24 md:py-32 container-x text-center">
      <div className="max-w-3xl mx-auto">
        <h2 className="heading-display text-4xl md:text-5xl lg:text-6xl text-ink">
          A Different <span className="italic text-gold">Patient Experience</span>
        </h2>
        <p className="mt-6 font-serif italic text-xl md:text-2xl text-ink/85">
          Care designed around you.
        </p>
        <p className="mt-6 text-ink/75 leading-relaxed">
          From your first consultation to your final result, every visit is intentionally calm, unrushed, and personal. Questions are encouraged. Decisions are collaborative. Your comfort is never an afterthought.
        </p>
      </div>
      <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
        <button type="button" onClick={openBooking} data-testid="about-book-cta" className="btn-primary">Book a Consultation</button>
        {/* <Link to="/services" data-testid="about-services-cta" className="btn-outline">View Services</Link> */}
      </div>
    </section>
  </div>
  );
};
